import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = process.env.REACT_APP_SITE_URL || '';
const SITE_NAME = 'Naviton Global';
const DEFAULT_DESCRIPTION = 'Naviton Global Pty Ltd — Australian international trade company specialising in import, export, and end-to-end supply chain solutions across global markets.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-image.png`;

export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  article = null,
  noindex = false,
}) => {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Australian Import & Export Solutions`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1536" />
      <meta property="og:image:height" content="1024" />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article-specific tags */}
      {article && (
        <meta property="article:published_time" content={article.date} />
      )}
      {article && <meta property="article:author" content={article.author} />}
      {article &&
        article.tags?.map((tag) => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}
    </Helmet>
  );
};

/**
 * Organisation structured data (JSON-LD) — add to the Home page only.
 */
export const OrganisationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organisation',
    name: 'Naviton Global Pty Ltd',
    url: SITE_URL || 'https://navitonglobal.com.au',
    logo: `${SITE_URL}/images/logo-dark.png`,
    description: DEFAULT_DESCRIPTION,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Sydney',
      addressCountry: 'AU',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+61-478822581',
      email: 'info@navitonglobal.com.au',
      contactType: 'customer service',
    },
    sameAs: [],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

/**
 * Blog article structured data (JSON-LD) — add to individual blog posts.
 */
export const ArticleSchema = ({ title, description, url, image, datePublished, author }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: `${SITE_URL}${url}`,
    image,
    datePublished,
    author: {
      '@type': 'Organisation',
      name: author || SITE_NAME,
    },
    publisher: {
      '@type': 'Organisation',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/images/logo-dark.png` },
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
